import { CommonModule, isPlatformServer } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnInit,
  Output,
  PLATFORM_ID,
  Signal,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { EventTemplateCardData } from '@models/design-templates/event-template-card-data.model';
import { EventTemplateFont } from '@models/design-templates/event-template-font.model';
import { EventTemplateVisual } from '@models/design-templates/event-template-visual.model';
import { EventAttendee } from '@models/event-attendance/event-attendee.model';
import { RSVPOptionType } from '@models/event-attendance/rsvp-option-type.enum';
import { EventConfirmationPageCardType } from '@models/events/enums/event-confirmation-page-card-type.enum';
import { EventAttendeeTicketOrder } from '@models/events/event-attendee-ticket-order.model';
import { EventAttendeeTicket } from '@models/events/event-attendee-ticket.model';
import { EventConfirmationPageCard } from '@models/events/event-confirmation-page-card.enum';
import { EventType } from '@models/events/event-type.enum';
import { EventWidget } from '@models/events/event-widget.enum';
import { Event } from '@models/events/event.model';
import { OneTimePricingPlan } from '@models/pricing-plans/one-time-pricing-plan.model';
import { User } from '@models/users/user.model';
import { GetBoughtTicketsDialog } from '@modules/events/dialogs/get-bought-tickets/get-bought-tickets.dialog';
import { RsvpQrCodeDialog } from '@modules/events/dialogs/rsvp-qr-code/rsvp-qr-code.dialog';
import { ShareButtonsComponent } from '@modules/shared/components/share-buttons/share-buttons.component';
import { AddToCalendarDialog } from '@modules/shared/dialogs/add-to-calendar/add-to-calendar.dialog';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth/auth.service';
import { EventTemplateService } from '@services/events/event-template.service';
import { EventService } from '@services/events/event.service';
import { PricingPlanService } from '@services/pricing-plans/pricing-plan.service';
import { NotificationService } from '@services/shared/notification.service';
import { ScreenWidthService } from '@services/shared/screen-width.service';
import { finalize } from 'rxjs';
import { FooterComponent } from '../../../../components/footer/footer.component';
import { SafeHtmlPipe } from '../../../../pipes/safe-html.pipe';

@Component({
  selector: 'app-premium-after-attend',
  standalone: true,
  imports: [CommonModule, FooterComponent, SafeHtmlPipe, ShareButtonsComponent],
  templateUrl: './premium-after-attend.component.html',
  styleUrl: './premium-after-attend.component.scss',
  providers: [
    EventTemplateService,
    EventService,
    NotificationService,
    ScreenWidthService,
    PricingPlanService,
  ],
})
export class PremiumAfterAttendComponent implements OnInit, AfterViewInit {
  private _rsvpType: RSVPOptionType = RSVPOptionType.YES;

  @Input() event?: Event;

  @Input() set rsvpType(value: RSVPOptionType) {
    this._rsvpType = value;
    this.eventConfirmationPage = this.findCardByRsvpType();
  }

  get rsvpType(): RSVPOptionType {
    return this._rsvpType;
  }

  @Input() onWaitlist: boolean = false;
  @Input() onPendingList: boolean = false;
  eventConfirmationPage?: EventConfirmationPageCard;
  loggedUser?: User;
  @Input() eventAttendee?: EventAttendee;
  @Input() isEdit: boolean = false;
  isDesktop: boolean = true;
  actionInProgress?: 'addToWallet';
  cards = [
    EventConfirmationPageCardType.CONFIRMATION,
    EventConfirmationPageCardType.SOCIAL_MEDIA,
    EventConfirmationPageCardType.CREATE_EVENT,
  ];
  @Input() withTickets?: boolean = false;
  @Input() ticketOrder?: EventAttendeeTicketOrder;
  @Output() close = new EventEmitter();
  myOrders?: EventAttendeeTicketOrder[];
  designTemplates: EventTemplateCardData[] = [];
  oneTimePricingPlans?: OneTimePricingPlan[];
  @ViewChild('backgroundOverlay')
  backgroundOverlay!: ElementRef<HTMLDivElement>;
  @ViewChild('effectOverlay')
  effectOverlay!: ElementRef<HTMLDivElement>;
  @ViewChild('dialogWrapper') dialogWrapper!: ElementRef<HTMLDivElement>;

  encodedMainImage: Signal<string> = computed(() => {
    return this.event ? this.event.getEncodedMainImage() : '';
  });

  // header scrolled fields
  scrolled = false;
  private scrollThreshold = 50; // px
  private scrollContainer?: HTMLElement | Window;
  private scrollListener?: () => void;

  isServer = false;

  constructor(
    private eventService: EventService,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private viewContainerRef: ViewContainerRef,
    private screenWidthService: ScreenWidthService,
    private authService: AuthService,
    private eventTemplateService: EventTemplateService,
    private pricingPlanService: PricingPlanService,
    private dialog: MatDialog,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) platformId: Object,
  ) {
    this.isServer = isPlatformServer(platformId);

    this.screenWidthService
      .isDesktop()
      .pipe(takeUntilDestroyed())
      .subscribe((isDesktop) => (this.isDesktop = isDesktop));

    this.authService.userSubject
      .pipe(takeUntilDestroyed())
      .subscribe((user) => (this.loggedUser = user));

    this.eventTemplateService.fetch().subscribe({
      next: (templates) => {
        this.designTemplates = templates;
      },
    });

    this.pricingPlanService.getOneTimePricingPlans().subscribe({
      next: (plans) => {
        this.oneTimePricingPlans = plans;
      },
      error: (err) => {},
    });
  }

  ngOnInit(): void {
    if (!this.event) return;
    this.getBoughtTickets();

    this.handleHeaderScrolled();
  }

  handleHeaderScrolled() {
    if (!this.isServer) {
      const container = document.querySelector('.mdc-dialog__surface');
      this.scrollContainer =
        container instanceof HTMLElement ? container : window;

      this.ngZone.runOutsideAngular(() => {
        this.scrollListener = () => {
          let currentScroll = 0;

          if (this.scrollContainer instanceof Window) {
            currentScroll =
              window.pageYOffset ||
              document.documentElement.scrollTop ||
              document.body.scrollTop ||
              0;
          } else {
            currentScroll = this.scrollContainer?.scrollTop || 0;
          }

          // Only trigger change detection when threshold state changes
          if (currentScroll > this.scrollThreshold !== this.scrolled) {
            this.ngZone.run(() => {
              this.scrolled = currentScroll > this.scrollThreshold;
            });
          }
        };

        this.scrollContainer?.addEventListener('scroll', this.scrollListener, {
          passive: true,
        });
      });
    }
  }

  ngAfterViewInit() {
    setTimeout(() => this.updateBackgroundHeight(), 50);
  }

  @HostListener('window:resize')
  onResize() {
    this.updateBackgroundHeight();
  }

  updateBackgroundHeight() {
    if (this.dialogWrapper) {
      const dialogHeight = this.dialogWrapper.nativeElement.offsetHeight;
      if (this.backgroundOverlay) {
        this.backgroundOverlay.nativeElement.style.minHeight = `${dialogHeight}px`;
      }

      if (this.effectOverlay) {
        this.effectOverlay.nativeElement.style.minHeight = `${dialogHeight}px`;
      }
    }
  }

  onClose() {
    this.close.emit();
  }

  getCtaText() {
    if (
      this.eventConfirmationPage &&
      this.eventConfirmationPage.ctaText &&
      this.eventConfirmationPage.ctaText.trim() !== ''
    ) {
      return this.eventConfirmationPage.ctaText;
    } else {
      if (this.hasConfirmed()) {
        return this.translate.instant('APP.EVENT_RSVP.SEE_YOUR_QR_CODE');
      } else {
        return this.translate.instant('APP.CHANGE_PAGES.TO_THE_EVENT');
      }
    }
  }

  hasConfirmed() {
    return (
      this.rsvpType === RSVPOptionType.YES ||
      this.rsvpType === RSVPOptionType.MAYBE
    );
  }

  getPremiumCustomDescription() {
    if (!this.event) return '';

    const card = this.event.confirmationPageCards?.find(
      (x) => x.type === EventConfirmationPageCardType.PREMIUM_CUSTOM_TEXT,
    );
    return card?.description || '';
  }

  findCardByType(type: EventConfirmationPageCardType) {
    if (!this.event) return;
    return this.event.confirmationPageCards?.find((x) => x.type === type);
  }

  get visualBackgroundUrl(): string | undefined {
    if (!this.visual?.background) {
      return undefined;
    }

    if (this.isDesktop || this.visual?.background?.mobileUrl === '') {
      return this.visual?.background?.url;
    } else {
      return this.visual?.background?.mobileUrl;
    }
  }

  get visualBackgroundHevcUrl(): string | undefined {
    if (!this.visual?.background) {
      return undefined;
    }

    if (this.isDesktop || this.visual?.background?.h265MobileUrl === '') {
      return this.visual?.background?.h265Url;
    } else {
      return this.visual?.background?.h265MobileUrl;
    }
  }

  get visualEffectUrl(): string | undefined {
    if (!this.visual?.effect) {
      return undefined;
    }

    if (this.isDesktop || this.visual?.effect?.mobileUrl === '') {
      return this.visual?.effect?.url;
    } else {
      return this.visual?.effect?.mobileUrl;
    }
  }

  get visualEffectHevcUrl(): string | undefined {
    if (!this.visual?.effect) {
      return undefined;
    }

    if (this.isDesktop || this.visual?.effect?.h265MobileUrl === '') {
      return this.visual?.effect?.h265Url;
    } else {
      return this.visual?.effect?.h265MobileUrl;
    }
  }

  addToWallet() {
    if (
      !this.event ||
      !this.attendee?.uuid ||
      this.actionInProgress === 'addToWallet'
    ) {
      return;
    }

    this.actionInProgress = 'addToWallet';

    const windowReference = window.open('about:blank', '_blank');

    this.eventService
      .getEventRsvpWalletPass(this.event.id, this.attendee.uuid)
      .pipe(finalize(() => (this.actionInProgress = undefined)))
      .subscribe({
        next: (passPageLink: string) => {
          if (windowReference) {
            windowReference.location.href = passPageLink;
          }
        },
        error: () => {
          if (windowReference) {
            windowReference.close();
          }
          this.notificationService.error(
            this.translate.instant('APP.EVENT_PAYMENT.COULD_NOT_DOWNLOAD'),
          );
        },
      });
  }

  getTitle() {
    if (this.withTickets) {
      return this.translate.instant('APP.EVENT_AFTER_ATTEND.TICKETS.TITLE');
    }
    if (
      this.eventConfirmationPage &&
      this.eventConfirmationPage.title &&
      this.eventConfirmationPage.title.trim() !== ''
    ) {
      return this.eventConfirmationPage.title;
    } else {
      if (this.event?.hasApp(EventWidget.BOOKING_SLOTS)) {
        return this.translate.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.YOU_ARE_SUCCESSFULLY_REGISTERED',
        );
      }
      switch (this.rsvpType) {
        case RSVPOptionType.YES:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.YOU_ARE_SUCCESSFULLY_REGISTERED',
          );
        case RSVPOptionType.NO:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.THANKS_FOR_UPDATE',
          );
        case RSVPOptionType.MAYBE:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.THANKS_FOR_UPDATE',
          );
        case RSVPOptionType.WAITLIST:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.THANKS_FOR_RESPONSE',
          );
        case RSVPOptionType.PENDING_APPROVAL:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.AWAITING_HOST_APPROVAL',
          );
        case RSVPOptionType.WITHDRAW:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.THANKS_FOR_UPDATE',
          );
        default:
          return '';
      }
    }
  }

  getDescription() {
    if (this.withTickets) {
      return this.translate.instant(
        'APP.EVENT_AFTER_ATTEND.TICKETS.DESCRIPTION',
        {
          eventName: this.event?.name,
        },
      );
    }
    if (
      this.eventConfirmationPage &&
      this.eventConfirmationPage.description &&
      this.eventConfirmationPage.description.trim() !== ''
    ) {
      return this.eventConfirmationPage.description;
    } else {
      switch (this.rsvpType) {
        case RSVPOptionType.YES:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.YES_DESCRIPTION',
            {
              eventName: this.event?.name,
            },
          );
        case RSVPOptionType.NO:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.NO_DESCRIPTION',
          );
        case RSVPOptionType.MAYBE:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.MAYBE_DESCRIPTION',
            {
              eventName: this.event?.name,
            },
          );
        case RSVPOptionType.WAITLIST:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.WAIT_LIST_DESCRIPTION',
            {
              eventName: this.event?.name,
            },
          );
        case RSVPOptionType.PENDING_APPROVAL:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.PENDING_DESCRIPTION',
            {
              eventName: this.event?.name,
            },
          );
        case RSVPOptionType.WITHDRAW:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.WITHDRAW_DESCRIPTION',
            {
              eventName: this.event?.name,
            },
          );
        default:
          return '';
      }
    }
  }

  getBoughtTickets() {
    if (this.loggedUser && this.event) {
      this.eventService
        .getTicketOrders(this.event.id, true)
        .subscribe((ticketOrders) => {
          if (ticketOrders && ticketOrders.length > 0) {
            this.myOrders = ticketOrders;
          }
        });
    }
  }
  seeTickets(mode: string) {
    let ticket: EventAttendeeTicket | undefined;
    let order: EventAttendeeTicketOrder | undefined = this.ticketOrder;

    if (mode === 'user') {
      ticket = this.ticketOrder?.eventAttendeeTickets.find(
        (x) => x.eventAttendee?.userId === this.loggedUser?.id,
      );
    } else if (mode === 'allOrders') {
      order = undefined;
    }

    this.openGetBoughtTicketsDialog(ticket, order);
  }

  findCardByRsvpType() {
    if (
      !this.event ||
      !this.event.confirmationPageCards ||
      this.event.confirmationPageCards.length === 0
    )
      return;
    let card = undefined;
    const pageCards = this.event.confirmationPageCards;
    switch (this.rsvpType) {
      case RSVPOptionType.YES:
        card = pageCards.find(
          (x) => x.type === EventConfirmationPageCardType.YES,
        );
        break;
      case RSVPOptionType.NO:
        card = pageCards.find(
          (x) => x.type === EventConfirmationPageCardType.NO,
        );
        break;
      case RSVPOptionType.MAYBE:
        card = pageCards.find(
          (x) => x.type === EventConfirmationPageCardType.MAYBE,
        );
        break;
      case RSVPOptionType.WAITLIST:
        card = pageCards.find(
          (x) => x.type === EventConfirmationPageCardType.WAITLIST,
        );
        break;
      case RSVPOptionType.PENDING_APPROVAL:
        card = pageCards.find(
          (x) => x.type === EventConfirmationPageCardType.PENDING,
        );
        break;
    }

    return card;
  }

  openGetBoughtTicketsDialog(
    ticket?: EventAttendeeTicket,
    order?: EventAttendeeTicketOrder,
  ) {
    const dialogRef = this.dialog.open(GetBoughtTicketsDialog, {
      maxWidth: '456px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      data: {
        event: this.event,
        orders: this.myOrders,
        loggedUser: this.loggedUser,
        ticket: ticket,
        order: order,
      },
      panelClass: ['normal-dialog', 'new-normal-dialog'],
      backdropClass: '',
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res && res.refresh) {
        this.getBoughtTickets();
      }
    });
  }

  addToCalendar() {
    this.dialog.open(AddToCalendarDialog, {
      maxWidth: '602px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      data: this.event,
      panelClass: ['normal-dialog', 'event-dialog', 'overlay-theme'],
    });
  }
  seeRsvpQRCode() {
    this.dialog.open(RsvpQrCodeDialog, {
      viewContainerRef: this.viewContainerRef,
      maxWidth: '602px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      data: {
        event: this.event,
        attendee: this.attendee,
      },
      panelClass: ['normal-dialog', 'secondary-dialog'],
      backdropClass: '',
    });
  }

  get attendee(): EventAttendee | undefined {
    return this.event?.getAttendee(this.loggedUser?.id) || this.eventAttendee;
  }

  get isSportpageEvent(): boolean {
    return this.event?.type === EventType.SPORTPAGE;
  }

  get font(): EventTemplateFont | undefined {
    return this.event?.eventTemplate?.font;
  }

  get visual(): EventTemplateVisual | undefined {
    return this.event?.eventTemplate?.visual;
  }

  get afterScreenRsvpType(): RSVPOptionType {
    if (this.rsvpType === RSVPOptionType.NO) {
      return this.rsvpType;
    }

    if (this.onPendingList) {
      return RSVPOptionType.PENDING_APPROVAL;
    } else if (this.onWaitlist) {
      return RSVPOptionType.WAITLIST;
    }

    return this.rsvpType;
  }

  ngOnDestroy() {
    if (this.scrollContainer && this.scrollListener) {
      this.scrollContainer?.removeEventListener('scroll', this.scrollListener);
    }
  }

  protected readonly EventConfirmationPageCardType =
    EventConfirmationPageCardType;
}
